/**
 * Utility helpers for generating and parsing job slugs used in pretty URLs.
 */

/**
 * Convert text into a URL-friendly slug segment.
 * @param {string} text
 * @returns {string}
 */
export function slugify(text = "") {
  return text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\u0300-\u036f]/g, "") // remove combining diacritics
    .replace(/[^a-z0-9\s-]/g, "") // remove invalid chars
    .replace(/\s+/g, "-") // collapse whitespace to hyphen
    .replace(/-+/g, "-") // collapse multiple hyphens
    .replace(/^-|-$/g, ""); // trim hyphens from ends
}

/**
 * Build the slug portion for a job details URL.
 * Produces `${slugifiedTitle}-${id}` when a title is available.
 * Falls back to just the id when the title is missing.
 * @param {string} title
 * @param {string|number} id
 * @returns {string}
 */
export function createJobSlug(title, id) {
  const normalizedId =
    id !== undefined && id !== null ? String(id).trim() : "";

  if (!normalizedId) {
    return slugify(title);
  }

  const titleSlug = slugify(title);
  return titleSlug ? `${titleSlug}-${normalizedId}` : normalizedId;
}

/**
 * Build the full job details route path using the provided title and id.
 * @param {string} title
 * @param {string|number} id
 * @returns {string}
 */
export function buildJobDetailsPath(title, id) {
  const slug = createJobSlug(title, id);
  return `/jobs/${slug || id || ""}`.replace(/\/+$/, "");
}

/**
 * Extract the numeric job id from a slug segment.
 * Accepts either a prettified slug or a raw id string.
 * @param {string} slug
 * @returns {string}
 */
export function extractIdFromSlug(slug = "") {
  const match = slug.toString().match(/(\d+)(?:\/)?$/);
  return match ? match[1] : slug;
}

